/* eslint-disable max-lines */

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import _ from 'underscore'
import moment from 'moment'
import { tz } from 'moment-timezone'

import { Panel } from 'components/Panel'
import HelpVideoLink from 'components/HelpVideoLink'
import LastUpdatedText from 'components/LastUpdatedText'
import Modal from 'components/Modal'

import ConsolidatedEventsBanner from 'pages/Device/ThermostatRthPanel/ConsolidatedEventsBanner'
import FullScreenPlot from 'pages/Device/ThermostatRthPanel/Chart/FullScreenPlot'
import ServerlessRthChart from 'pages/Device/ThermostatRthPanel/ServerlessRthChart'
import RotateDeviceMessage from 'pages/shared/RotateDeviceMessage'
import RthHeaderControl from 'pages/shared/Device/RthHeaderControl'
import { getDefaultSelectedSeriesItems } from 'pages/Device/ThermostatRthPanel/Chart/plotHelper'
import { isNoDataAvailable } from 'pages/Device/ThermostatRthPanel/Chart/rthDataParser'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'

import useDeviceConsolidatedEvents from 'hooks/useDeviceConsolidatedEvents'
import useOrientation from 'hooks/useOrientation'
import useServerlessRth from 'hooks/useServerlessRth'

import GA4 from 'helpers/ga4'
import BrowserCompatibility from 'helpers/browserCompatibility'
import URLParamsHelper from 'helpers/url_params_helper'
import Apptentive from 'helpers/apptentive'
import { longDate } from 'helpers/formatters'
import { ExportGraphAsImage } from 'helpers/plotly/exportGraphAsImage'

import { Device } from 'types/Device'

import { screenSize, screenWidthBreakpoints } from 'styles/shared'
import { color } from 'styles/colors'

import HomeownerPanelHeaderNotification from 'pages/shared/HomeownerAlert/HomeownerPanelHeaderNotification'

type Ref = HTMLDivElement

type Props = {
  isActive: boolean
  isHehm?: boolean
  device: Pick<Device, 'dealerId' | 'deviceId' | 'deviceType'>
  thermostatRthDate: string
  timeZone: string
  reportCache?: Record<string, unknown>
  updateThermostatRthDateCallback: (day: string) => void
}

const ThermostatRthPanel = forwardRef<Ref, Props>(({ isActive, isHehm = false, device, thermostatRthDate, timeZone, reportCache = {}, updateThermostatRthDateCallback }: Props, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const getThermostatRthDate = useCallback(() => {
    return (!_.isEmpty(thermostatRthDate) ? tz(thermostatRthDate, 'MM-DD-YYYY', timeZone) : tz(timeZone)).format('YYYY-MM-DD')
  }, [timeZone, thermostatRthDate])

  const { dealerId, deviceId, deviceType } = device

  const rthDate = getThermostatRthDate()
  const [isZoomed, setIsZoomed] = useState(false)
  const [isFullScreenPlotZoomed, setIsFullScreenPlotZoomed] = useState(false)
  const [freezeMouseHover, setFreezeMouseHover] = useState(false)
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())
  const [showFullScreen, setShowFullScreen] = useState(false)
  const { consolidatedEvents } = useDeviceConsolidatedEvents(deviceId, moment(rthDate).format('MM-DD-YYYY'))
  const { InfoIcon } = usePremiumFeaturesContext()

  const startTime = tz(`${rthDate}`, 'YYYY-MM-DD', timeZone).startOf('day').format('YYYY-MM-DD HH:mm:ss')
  const endTime = tz(`${rthDate}`, 'YYYY-MM-DD', timeZone).endOf('day').format('YYYY-MM-DD HH:mm:ss')

  const { rthData: rthDataServerless, isRTHDataLoaded: isRTHDataServerlessLoaded } = useServerlessRth(dealerId, deviceId, rthDate, rthDate, timeZone, refreshTimestamp)

  const isMobile = BrowserCompatibility.isMobile(navigator.userAgent)
  const { isPortrait } = useOrientation()

  const onResetZoom = () => {
    setIsZoomed(false)
  }

  const onDateChangeCallback = useCallback((date) => {
    updateThermostatRthDateCallback(tz(date, timeZone).format('MM-DD-YYYY'))
    setIsZoomed(false)
  }, [timeZone, updateThermostatRthDateCallback])
  const onRefreshCallback = useCallback(() => {
    setRefreshTimestamp(moment().valueOf())
    setIsZoomed(false)
  }, [])

  const defaultSelectedLegendItems = useMemo(() => getDefaultSelectedSeriesItems(deviceId, rthDataServerless, isHehm), [deviceId, rthDataServerless, isHehm])
  const [selectedLegends, setSelectedLegends] = useState({ ...defaultSelectedLegendItems })

  const updateSelectedLegends = (selectedLegends) => {
    setSelectedLegends(selectedLegends)
  }

  const onExportCallback = (selectedDate) => {
    const chartId = 'plotly-serverless-rth-chart'
    const plotId = showFullScreen ? 'full-plotly' : chartId
    const plotlyDiv = document.getElementById(plotId)

    ExportGraphAsImage(plotlyDiv, canvasRef.current, 'runtime_' + deviceId + '_' + selectedDate, longDate(selectedDate))
  }

  const onFreezeMouseHoverCallback = useCallback((freeze: boolean) => setFreezeMouseHover(freeze), [])
  const onZoomedCallback = useCallback((isZoomed) => {
    GA4.sendEvent({ category: 'Customer Detail Page', action: isZoomed ? 'RTH Zoom in' : 'RTH Reset Zoom' })
    setIsZoomed(isZoomed)
  }, [])

  const fullScreenIconClicked = () => {
    GA4.sendEvent({ category: 'Customer Detail Page', action: 'Expand RTH' })

    Apptentive.sendEvent('expand_rth_fullscreen')

    setShowFullScreen(true)
  }

  useEffect(() => {
    updateSelectedLegends(selectedLegends)
  }, [selectedLegends])

  useEffect(() => {
    if (isActive) {
      URLParamsHelper.update('thermostatRthDate', tz(rthDate, timeZone).format('MM-DD-YYYY'))
    }
  }, [isActive, rthDate, timeZone])

  return (
    <S.Container className="device-panel runtime-history-container" ref={ref} >
      <S.Panel id="thermostat-runtime-history" scrollToSection>
        <S.PanelHeader>
          <S.PanelTitle>{isHehm ? 'Runtime History' : 'Thermostat Runtime History'}
            <InfoIcon featureCode="extended_rthg" />
            <HelpVideoLink videoKey="rth" />
          </S.PanelTitle>
          {!isHehm && <HomeownerPanelHeaderNotification />}
        </S.PanelHeader>
        {isHehm && rthDate === moment().format('YYYY-MM-DD') && <S.LastUpdatedText timestamp={Date.now()} timeOnly />}
        {!isHehm && consolidatedEvents && consolidatedEvents?.length > 0 && <ConsolidatedEventsBanner consolidatedEvents={consolidatedEvents} />}

        {isMobile && isPortrait
          ? (
            <RotateDeviceMessage />
          )
          : (
            <>
              <RthHeaderControl
                date={rthDate}
                displayResetZoom={isZoomed}
                dateChangedCallback={onDateChangeCallback}
                zoomResetCallback={onResetZoom}
                refreshCallback={onRefreshCallback}
                rthVariant="RTH"
                freezeMouseHover={freezeMouseHover}
                isNoData={isNoDataAvailable(rthDataServerless)}
                isRTHDataLoaded={isRTHDataServerlessLoaded}
                onExportCallback={onExportCallback}
                isFullScreen={showFullScreen}
                fullScreenIconClickedCallback={fullScreenIconClicked}
                displayHomeownerNotification
              />
              <S.ServerlessRthChart
                rthDate={rthDate}
                startTime={startTime}
                endTime={endTime}
                timeZone={timeZone}
                deviceId={deviceId}
                showFullScreen={showFullScreen}
                isControlHub={deviceType === 'control_hub'}
                isHehm={isHehm}
                updateSelectedLegendsCallback={updateSelectedLegends}
                zoomedCallback={onZoomedCallback}
                freezeMouseHoverCallback={onFreezeMouseHoverCallback}
                freezeMouseHover={freezeMouseHover}
                isZoomed={isZoomed}
                rthData={rthDataServerless}
                isRTHDataLoaded={isRTHDataServerlessLoaded}
                reportCache={reportCache}
              />
              <Modal
                closeCallback={() => setShowFullScreen(false)}
                isDisplayed={showFullScreen}
                dataTestID="fullscreen_rth_close"
                fullScreen
              >
                <S.RthHeaderContainer>
                  <RthHeaderControl
                    date={rthDate}
                    displayResetZoom={isFullScreenPlotZoomed}
                    dateChangedCallback={onDateChangeCallback}
                    zoomResetCallback={() => setIsFullScreenPlotZoomed(false)}
                    refreshCallback={onRefreshCallback}
                    rthVariant="RTH"
                    freezeMouseHover={freezeMouseHover}
                    isNoData={isNoDataAvailable(rthDataServerless)}
                    isRTHDataLoaded={isRTHDataServerlessLoaded}
                    onExportCallback={onExportCallback}
                    isFullScreen={showFullScreen}
                    fullScreenIconClickedCallback={fullScreenIconClicked}
                  />
                </S.RthHeaderContainer>
                <FullScreenPlot
                  startTime={startTime}
                  endTime={endTime}
                  chartDivId="full-plotly"
                  timeZone={timeZone}
                  deviceId={deviceId}
                  isZoomed={isFullScreenPlotZoomed}
                  zoomedCallback={(isZoomed) => { setIsFullScreenPlotZoomed(isZoomed) }}
                  rthData={rthDataServerless}
                  isRTHDataLoaded={isRTHDataServerlessLoaded}
                  updateSelectedLegendsCallback={updateSelectedLegends}
                  isHehm={isHehm}
                />
              </Modal>
              <S.Canvas ref={canvasRef} />
            </>
          )}
      </S.Panel>
    </S.Container>
  )
})

export default ThermostatRthPanel

const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
  `,
  PanelTitle: styled(Panel.Title)`
    display: flex;
    align-items: center;`,
  PanelHeader: styled(Panel.Header)`
    padding-right: 0;

    @media (max-width: ${screenWidthBreakpoints.devicePanelHeaderShortVersionBreakpoint}px) {
      justify-content: flex-start;
    }
  `,
  Container: styled.div`
    width: 100%;

    @media ${screenSize.small} {
      min-height: 0;
    }
  `,
  RthHeaderContainer: styled.div`
    padding-right: 30px;
  `,
  ServerlessRthChart: styled(ServerlessRthChart)`
    width: 100%;
    height: 100%;
  `,
  HomeownerAlerted: styled(Panel.Header)`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    background-color: ${color('paneBackground')};
    height: 28px;
    white-space: no-wrap;
    color: ${color('homeownerAlertText')};
    font-style: italic;
    margin: 10px 10px;
    border-radius: 10px;
  `,
  Canvas: styled.canvas`
    display: none;
  `,
  LastUpdatedText: styled(LastUpdatedText)`
    margin-bottom: -28px;
  `
}
